import { useQuery } from '@apollo/client';
import React from 'react';

import { CopyTextButton } from '@eluve/blocks';
import {
  Badge,
  ColDefBuilder,
  DataTable,
  Heading,
  Link,
  Stack,
  Text,
  makeDefaultSortableColumnDef,
  makeVisibilityToggleColumnDef,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

import {
  ModelArgsColDef,
  ModelArgsColDefProps,
} from '../components/ModelArgsColDef';
import {
  PromptTemplateColDef,
  PromptTemplateColDefProps,
} from '../components/PromptTemplateColDef';

type SummaryFeedbackRow = {
  id: string;
  appointmentId: string;
  type: string | null;
  comment: string | null;
  tags: string[];
  createdAt: string;
  rating: number | null;
} & PromptTemplateColDefProps &
  ModelArgsColDefProps;

const getFeedbackQuery = graphql(`
  query getFeedback {
    summaryFeedback(orderBy: { createdAt: DESC }) {
      id
      createdAt
      type
      comment
      rating
      appointmentId
      summaryFeedbackTags {
        feedbackTag {
          name
        }
      }
      llm_output {
        modelPromptTemplate {
          id
          model_args {
            id
            args
          }
          template {
            name
            id
          }
        }
      }
    }
  }
`);

const columns = new ColDefBuilder<SummaryFeedbackRow>()
  .colDef({
    ...makeDefaultSortableColumnDef('appointmentId', 'Appointment'),
    id: 'appointmentId',
    accessorFn: (row) => row.appointmentId,
    cell: ({ row }) => (
      <Stack direction="horizontal" fullWidth={false} gap={2}>
        <Link
          to={`../appointments/${row.original.appointmentId}`}
          relative="path"
        >
          {row.original.appointmentId}
        </Link>
        <CopyTextButton
          copyText={row.original.appointmentId}
          variant="ghost"
          className="hover:bg-transparent"
        />
      </Stack>
    ),
  })
  .colDef(
    makeVisibilityToggleColumnDef({
      ...makeDefaultSortableColumnDef('id', 'Feedback'),
      id: 'feedbackId',
      accessorFn: (row) => row.id,
      hiddenCell: ({ row }) => (
        <Stack align="center" justify="center" fullWidth={false}>
          <CopyTextButton copyText={row.original.id} />
        </Stack>
      ),
      cell: ({ row }) => {
        return row.original.id;
      },
    }),
  )
  .defaultSortable('type', {
    cellRenderer: ({ type }) => (
      <Text
        className={
          type === 'POSITIVE' ? 'text-text-positive' : 'text-text-negative'
        }
      >
        {type}
      </Text>
    ),
  })
  .defaultSortable('rating')
  .defaultSortable('comment')
  .colDef({
    accessorKey: 'tags',
    header: 'Tags',
    accessorFn: (row) => row.tags.join(', '),
    cell: ({ row }) => {
      return (
        <>
          {row.original.tags.map((tag) => (
            <Badge variant="outline" className="my-1" key={tag}>
              {tag}
            </Badge>
          ))}
        </>
      );
    },
  })
  .colDef(PromptTemplateColDef())
  .colDef(ModelArgsColDef())
  .dateSortable('createdAt', 'Created At')
  .build();

export const AdminFeedbackDash: React.FC = () => {
  const { data } = useQuery(getFeedbackQuery);

  return (
    <div>
      <Heading size="2xl" className="pb-2">
        Feedback
      </Heading>
      <DataTable
        data={
          data?.summaryFeedback?.map((sf) => {
            const output = sf.llm_output?.modelPromptTemplate;
            return {
              id: sf.id,
              appointmentId: sf.appointmentId,
              comment: sf.comment,
              type: sf.type,
              createdAt: sf.createdAt,
              tags: (sf.summaryFeedbackTags || []).map(
                (sft) => sft.feedbackTag.name,
              ),
              rating: sf.rating,
              promptTemplate: output?.template || null,
              modelArgs: output?.model_args || null,
            };
          }) ?? []
        }
        enableGlobalSearch
        columns={columns}
      />
    </div>
  );
};
